/* eslint-disable @typescript-eslint/ban-ts-comment */
import { getSessionIDAsync, getAnonymousIDAsync, getInstanceIDAsync } from 'redline-client-sdk';

// --- Data Formatting ---
type FormattedValue = string | number | boolean | FormattedData | FormattedValue[];
type FormattedData = { [key: string]: FormattedValue };

/**
 * Recursively formats data for AppsFlyer payload:
 * - Replaces dots (.) in object keys with underscores (_).
 * - Converts non-string/number/boolean primitive values (like null, undefined) to empty strings.
 * - Preserves arrays and nested objects, applying formatting recursively.
 * @param data The data to format (can be any type).
 * @returns The formatted data.
 */
const formatNestedData = (data: unknown): FormattedValue => {
  if (Array.isArray(data)) {
    return data.map((item) => formatNestedData(item));
  } else if (typeof data === 'object' && data !== null) {
    return Object.entries(data).reduce((acc: FormattedData, [key, value]) => {
      const formattedKey = key.replace(/\./g, '_');

      if (typeof value === 'object' && value !== null) {
        acc[formattedKey] = formatNestedData(value);
      } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        acc[formattedKey] = value;
      } else {
        // Coerce other types (null, undefined, etc.) to empty string.
        acc[formattedKey] = '';
      }
      return acc;
    }, {});
  } else if (typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean') {
    return data;
  } else {
    // Handle standalone null, undefined, etc. -> convert to empty string
    return '';
  }
};

// --- Identifier Fetching ---
const getIdentifiers = async () => {
  try {
    const [sessionId, anonymousId, instanceId] = await Promise.all([
      getSessionIDAsync(),
      getAnonymousIDAsync(),
      getInstanceIDAsync(),
    ]);
    return { sessionId, anonymousId, instanceId };
  } catch (error) {
    console.error('Error fetching identifiers:', error);
    return {
      sessionId: null,
      anonymousId: null,
      instanceId: null,
    };
  }
};

// --- Bridge Logic ---
export const appsFlyerBridge = () => {
  const logEvent = async (event: string, data: Record<string, unknown>) => {
    // @ts-ignore
    if (!window.bridge || !window.bridge.isWebViewBridgeAvailable) return;

    try {
      const formattedEvent = event.replace(/\./g, '_');
      const [formattedData, identifiers] = await Promise.all([Promise.resolve(formatNestedData(data)), getIdentifiers()]);

      const payload = {
        identifiers,
        ...(formattedData as FormattedData),
      };

      // @ts-ignore
      await window.bridge.appsFlyerLogEvent(formattedEvent, payload);
    } catch (error) {
      console.error('Error logging event to AppsFlyer:', error);
    }
  };

  return { logEvent };
};
